import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type { ReleaseReport } from "../../shared/types/api";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  Select,
  StatusBadge,
} from "../../shared/ui";

const WINDOWS: [string, number][] = [
  ["Last 90 days", 90],
  ["Last 6 months", 182],
  ["Last year", 365],
  ["Last 2 years", 730],
];

/** A figure with what it was measured over — a rate without its denominator says very little. */
function Figure({
  label,
  value,
  outOf,
  tone = "gray",
}: {
  label: string;
  value: string;
  outOf?: string;
  tone?: "green" | "amber" | "red" | "gray";
}) {
  const colour =
    tone === "green"
      ? "text-green-700"
      : tone === "amber"
        ? "text-amber-700"
        : tone === "red"
          ? "text-red-700"
          : "text-ink-900";
  return (
    <div className="rounded-lg border border-ink-200 p-3">
      <p className="text-xs text-ink-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${colour}`}>{value}</p>
      {outOf && <p className="mt-0.5 text-xs text-ink-500">{outOf}</p>}
    </div>
  );
}

/**
 * How this project's releases have actually gone (§74, §75).
 *
 * <p>Every figure is shown with what it was measured over. "100% on time" across one release and
 * across forty are different facts, and a bare percentage makes them look the same — which is how
 * a team talks itself into confidence on a sample of one.
 *
 * <p>Rates are null rather than zero when nothing has settled, and render as "—". Zero percent
 * reads as "everything failed"; nothing has finished yet is a different statement.
 */
export function ReleaseReportPanel({ projectId }: { projectId: string }) {
  const [days, setDays] = useState(365);

  const to = new Date();
  const from = new Date(to.getTime() - days * 86_400_000);

  const report = useQuery({
    queryKey: ["release-report", projectId, days],
    queryFn: () =>
      api.get<ReleaseReport>(
        `/api/v1/projects/${projectId}/release-report`
          + `?from=${from.toISOString()}&to=${to.toISOString()}`,
      ),
  });

  const data = report.data;
  const rate = (value: number | null) => (value == null ? "—" : `${value}%`);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Release performance"
          subtitle="Outcomes, timeliness and how much moved after the freeze."
          action={
            <Select
              value={String(days)}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {WINDOWS.map(([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          }
        />
        <CardBody>
          {report.isPending ? (
            <LoadingState label="Reading the release history…" />
          ) : !data ? (
            <EmptyState title="No data" message="Nothing to report for this window." />
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Figure
                label="Released cleanly"
                value={rate(data.outcomes.successRate)}
                outOf={`${data.outcomes.completed} of ${data.outcomes.settled} finished`}
                tone={
                  data.outcomes.successRate == null
                    ? "gray"
                    : data.outcomes.successRate >= 90
                      ? "green"
                      : data.outcomes.successRate >= 70
                        ? "amber"
                        : "red"
                }
              />
              <Figure
                label="On time"
                value={rate(data.timeliness.onTimeRate)}
                outOf={
                  data.timeliness.measured === 0
                    ? "no dated releases yet"
                    : `${data.timeliness.onTime} of ${data.timeliness.measured} released`
                }
                tone={
                  data.timeliness.onTimeRate == null
                    ? "gray"
                    : data.timeliness.onTimeRate >= 80
                      ? "green"
                      : "amber"
                }
              />
              <Figure
                label="QA pass rate"
                value={rate(data.qa.passRate)}
                outOf={`${data.qa.passed} passed · ${data.qa.failed} failed`}
                tone={
                  data.qa.passRate == null ? "gray" : data.qa.passRate >= 90 ? "green" : "amber"
                }
              />
              <Figure
                label="Rolled back"
                value={String(data.outcomes.rolledBack + data.outcomes.failed)}
                outOf="failed or rolled back"
                tone={data.outcomes.rolledBack + data.outcomes.failed > 0 ? "red" : "green"}
              />
            </div>
          )}
        </CardBody>
      </Card>

      {data && data.deployments.length > 0 && (
        <Card>
          <CardHeader
            title="Deployment reliability"
            subtitle="Attempts against successes. One attempt per success is healthy; more means the deployment is not."
          />
          <CardBody>
            <ul className="divide-y divide-ink-100">
              {data.deployments.map((row) => (
                <li
                  key={row.environment}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-ink-900">{row.environment}</p>
                    <p className="text-xs text-ink-500">
                      {row.attempts} attempts · {row.succeeded} succeeded · {row.failed} failed
                    </p>
                  </div>
                  <StatusBadge
                    tone={
                      row.attemptsPerSuccess == null
                        ? "red"
                        : row.attemptsPerSuccess <= 1.2
                          ? "green"
                          : row.attemptsPerSuccess <= 2
                            ? "amber"
                            : "red"
                    }
                  >
                    {row.attemptsPerSuccess == null
                      ? "never succeeded"
                      : `${row.attemptsPerSuccess} tries per success`}
                  </StatusBadge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {data && data.scopeChurn.length > 0 && (
        <Card>
          <CardHeader
            title="Scope moved after the freeze"
            subtitle="§13: scope does change. What matters is that it is visible and has a reason."
          />
          <CardBody className="space-y-3">
            <ul className="divide-y divide-ink-100">
              {data.scopeChurn.map((row) => (
                <li key={row.releaseId} className="flex items-center justify-between py-2">
                  <span className="text-sm text-ink-900">
                    <span className="font-mono text-xs text-ink-500">{row.releaseKey}</span>{" "}
                    {row.version}
                  </span>
                  <span className="text-xs text-ink-600">
                    +{row.addedAfterFreeze} / −{row.removedAfterFreeze}
                  </span>
                </li>
              ))}
            </ul>

            {data.changeReasons.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-t border-ink-100 pt-3">
                {data.changeReasons.map((reason) => (
                  <span
                    key={reason.label}
                    className="rounded-full bg-ink-100 px-2.5 py-1 text-xs text-ink-700"
                  >
                    {reason.label}
                    <span className="ml-1 tabular-nums opacity-70">{reason.total}</span>
                  </span>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {data && data.gateOverrides.length > 0 && (
        <Card>
          <CardHeader
            title="Gates waived"
            subtitle="A waiver is legitimate. A pattern of the same waiver is a process that does not fit."
          />
          <CardBody>
            <ul className="divide-y divide-ink-100">
              {data.gateOverrides.map((row) => (
                <li key={row.label} className="flex items-center justify-between py-2">
                  <span className="text-sm text-ink-900">{row.label}</span>
                  <StatusBadge tone={row.total > 2 ? "amber" : "gray"}>
                    {row.total} {row.total === 1 ? "time" : "times"}
                  </StatusBadge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
